import { concat, filter, includes, reduce, flatMap, union } from 'lodash';
import { getCurrentUser } from 'utils/localStorage';

export const model = ({ models }, { modelId }) => models.byId[modelId];
export const models = ({ models }) => models;

export const modelWorkingState = ({ modelsWorkingState }, { modelId }) =>
  modelsWorkingState.byId[modelId];

export const modelViewer = ({ modelsViewer }, { modelId }) =>
  modelsViewer.byId[modelId];

export const orderedModelsBySeriesAndBodyStyles = ({ models, series }) => {
  if (models.indexIds.length > 0 && series.indexIds.length > 0) {
    const ids = series.indexIds.reduce((modelIds, seriesId) => {
      const serie = series.byId[seriesId];
      const seriesBodyStyleIds = serie.bodyStyles;

      const serieModels = Object.values(models.byId).filter(
        model => model.seriesId === seriesId,
      );

      const orderedModelIds = seriesBodyStyleIds.reduce(
        (modelsArray, bodyStyleId) => {
          let modelsByBodyStyle = serieModels.reduce((modelIds, model) => {
            return model.bodyStyleId === bodyStyleId
              ? [...modelIds, model.id]
              : modelIds;
          }, []);
          return [...modelsArray, ...modelsByBodyStyle];
        },
        [],
      );
      return [...modelIds, ...orderedModelIds];
    }, []);
    return ids;
  }
};

export const currentSelectedQuantity = (
  { modelsWorkingState, templatesWorkingState },
  { modelId },
) => {
  const model = modelsWorkingState.byId[modelId];
  let currentSelectedQuantity = 0;
  if (model && model.templatesWorkingState) {
    currentSelectedQuantity = model.templatesWorkingState.reduce(
      (total, id) =>
        total +
        Object.values(
          templatesWorkingState.byId[id].prioritiesToQuantitiesMap,
        ).reduce(
          (accumulator, currentQuantity) =>
            accumulator + parseInt(currentQuantity, 10),
          0,
        ),
      0,
    );
  }
  return currentSelectedQuantity;
};

export const suggestedTemplateQuantity = (
  { models, templates },
  { modelId },
) => {
  const model = models.byId[modelId];
  if (model && model.templateIds && templates.byId) {
    return model.templateIds.reduce(
      (total, templateId) =>
        (total += templates.byId[templateId].suggestedQuant),
      0,
    );
  } else {
    return 0;
  }
};

export const modelRemainingQuota = ({ modelsWorkingState }, { modelId }) =>
  modelsWorkingState.byId[modelId]
    ? parseInt(modelsWorkingState.byId[modelId].remainingQuota, 10)
    : 0;

export const inReviewTemplateIds = (
  { modelsWorkingState, templatesWorkingState },
  { modelId },
) => {
  const model = modelsWorkingState.byId[modelId];
  if (model && model.templatesWorkingState) {
    return model.templatesWorkingState.filter(
      id => templatesWorkingState.byId[id].status === 'inReview',
    );
  } else {
    return [];
  }
};

export const selectedTemplatesByPriceBand = (
  {
    models,
    modelsWorkingState,
    templatesWorkingState,
    templates,
    templatesSubmitted,
  },
  { modelId },
) => {
  const modelWorkingState = modelsWorkingState.byId[modelId];
  const modelStaticData = models.byId[modelId];

  let dataByPriceBand = {};
  if (modelStaticData && modelWorkingState && templates.byId) {
    modelStaticData.priceBands.forEach(
      priceBand => (dataByPriceBand[priceBand.id] = 0),
    );
    if (dataByPriceBand) {
      modelWorkingState.templatesWorkingState.forEach(templateId => {
        if (templates.byId[templateId]) {
          dataByPriceBand[
            templates.byId[templateId].priceBandId
          ] += Object.values(
            templatesWorkingState.byId[templateId].prioritiesToQuantitiesMap,
          ).reduce((total, quantity) => (total += quantity), 0);
        }
      });

      // TODO: Figure out why this guard is needed
      modelWorkingState.templatesSubmitted =
        modelWorkingState.templatesSubmitted || [];
      modelWorkingState.templatesSubmitted.forEach(templateId => {
        if (templates.byId[templateId]) {
          dataByPriceBand[
            templates.byId[templateId].priceBandId
          ] += Object.values(
            templatesSubmitted.byId[templateId].prioritiesToQuantitiesMap,
          ).reduce((total, quantity) => (total += parseInt(quantity, 10)), 0);
        }
      });
    }
  }

  return dataByPriceBand;
};

export const suggestedTemplatesByPriceBand = (
  { models, templates },
  { modelId },
) => {
  const modelStaticData = models.byId[modelId];
  let dataByPriceBand = {};
  if (modelStaticData && modelStaticData.templateIds) {
    modelStaticData.priceBands.forEach(
      priceBand => (dataByPriceBand[priceBand.id] = 0),
    );
    modelStaticData.templateIds.forEach(
      templateId =>
        (dataByPriceBand[templates.byId[templateId].priceBandId] +=
          templates.byId[templateId].suggestedQuant),
    );
  }

  return dataByPriceBand;
};

export const inCartQuantity = (
  { modelsWorkingState, templatesWorkingState },
  { modelId },
) => {
  const model = modelsWorkingState.byId[modelId];
  let inCartQuantity = 0;
  if (model && model.templatesWorkingState) {
    inCartQuantity = model.templatesWorkingState
      .map(templateId => templatesWorkingState.byId[templateId])
      .filter(template => template.status === 'inCart');

    inCartQuantity = flatMap(inCartQuantity, template =>
      Object.values(template.prioritiesToQuantitiesMap),
    ).reduce((total, quantity) => total + parseInt(quantity, 10), 0);
  }
  return inCartQuantity;
};

export const denormalizeModelWorkingState = ({
  modelId,
  modelsWorkingState,
  templatesWorkingState,
}) => {
  const model = modelsWorkingState.byId[modelId];
  if (model && model.templatesWorkingState) {
    const modelTemplatesWorkingState = model.templatesWorkingState.map(
      id => templatesWorkingState.byId[id],
    );
    return {
      id: model.id,
      templatesWorkingState: modelTemplatesWorkingState,
    };
  }
  return { templatesWorkingState: {} };
};

export const modelWorkingStateDenormalized = (
  { modelsWorkingState, templatesWorkingState },
  { modelId },
) =>
  denormalizeModelWorkingState({
    modelId,
    modelsWorkingState,
    templatesWorkingState,
  });

export const getPreviousModelWorkingStateDenormalized = ({
  modelsWorkingState,
  templatesWorkingState,
}) => modelId =>
  denormalizeModelWorkingState({
    modelId,
    modelsWorkingState,
    templatesWorkingState,
  });

// Waiting for IPA data consistency -> using
// modelSubmittedTotalWorkingStateCalculated instead
export const modelSubmittedTotal = ({ models }, { modelId }) => {
  const model = models.byId[modelId];
  if (model) {
    return parseInt(model.quota, 10) - parseInt(model.unsubmitted, 10);
  }
  return 0;
};

export const modelSubmittedTotalWorkingStateCalculated = (
  { modelsWorkingState, templatesSubmitted },
  { modelId },
) => {
  const modelWorkingState = modelsWorkingState.byId[modelId];
  if (modelWorkingState && modelWorkingState.templatesSubmitted) {
    const submittedTemplateQuantities = modelWorkingState.templatesSubmitted.map(
      tid =>
        Object.values(
          templatesSubmitted.byId[tid].prioritiesToQuantitiesMap,
        ).reduce(
          (accumulator, currentMapping) =>
            accumulator + parseInt(currentMapping, 10),
          0,
        ),
    );
    return Object.values(submittedTemplateQuantities).reduce(
      (accumulator, currentMapping) =>
        accumulator + parseInt(currentMapping, 10),
      0,
    );
  }
  return 0;
};

export const unsubmittedQuota = ({ models }, { modelId }) =>
  models.byId[modelId] ? parseInt(models.byId[modelId].unsubmitted, 10) : 0;

export const totalQuota = ({ models }, { modelId }) =>
  models.byId[modelId] ? parseInt(models.byId[modelId].quota, 10) : 0;

export const optionsForModel = (
  {
    functionalOptionCodeMappings,
    functionalOptionCodes,
    functionalGroups,
    templates,
    interiors,
    paints,
    roofAndMirrorCaps,
    wheels,
    trims,
  },
  { model },
) => {
  if (model === undefined) {
    return [];
  }
  const filteredFunctionalOptionCodeMappingsForModel = model.functionalOptionCodeMappings.map(
    optionCode => functionalOptionCodeMappings.byId[optionCode],
  );

  let functionalOptions = reduce(
    filteredFunctionalOptionCodeMappingsForModel,
    (result, fid, key) => {
      let functionalGroupsForOption = reduce(
        functionalGroups.byId,
        function(acc, fg) {
          if (includes(fg.functionalGroupCodes, fid.id)) {
            return acc.concat(fg.id);
          } else {
            return acc;
          }
        },
        [],
      );

      let templatesForOption = reduce(
        functionalGroups.byId,
        function(acc, fg) {
          if (includes(fg.functionalGroupCodes, fid.id)) {
            return acc.concat(fg.templates);
          } else {
            return acc;
          }
        },
        [],
      );

      let option = {
        code: fid.id,
        id: fid.id + 'functional',
        description: fid.description,
        optionType: 'functional',
        functionalGroupIds: functionalGroupsForOption,
        templateIds: templatesForOption,
      };
      return result.concat(option);
    },
    [],
  );

  let modelId = model.id;
  let templatesForModel = filter(templates.byId, ['modelId', modelId]);

  let interiorOptions = reduce(
    model.availableInteriors,
    (acc, code) => {
      let details = interiors.byId[code];
      let templates = reduce(
        templatesForModel,
        (acc, template) => {
          if (template.designPackage.interiorOption.id === code) {
            let tIds = union(acc.tIds, [template.id]);
            let fIds = union(acc.fgIds, [template.functionalGroupId]);
            return {
              tIds: tIds,
              fgIds: fIds,
            };
          } else {
            return acc;
          }
        },
        { tIds: [], fgIds: [] },
      );

      if (templates.tIds.length > 0) {
        return acc.concat({
          code: details.id,
          id: details.id + 'interior',
          description: details.description,
          optionType: 'interior',
          functionalGroupIds: templates.fgIds,
          templateIds: templates.tIds,
        });
      } else {
        return acc;
      }
    },
    [],
  );

  let paintOptions = reduce(
    model.availablePaints,
    (acc, code) => {
      let details = paints.byId[code];
      let templates = reduce(
        templatesForModel,
        (acc, template) => {
          if (template.designPackage.paintOption.id === code) {
            let tIds = union(acc.tIds, [template.id]);
            let fIds = union(acc.fgIds, [template.functionalGroupId]);
            return {
              tIds: tIds,
              fgIds: fIds,
            };
          } else {
            return acc;
          }
        },
        { tIds: [], fgIds: [] },
      );

      if (templates.tIds.length > 0) {
        return acc.concat({
          code: details.id,
          id: details.id + 'paint',
          description: details.description,
          optionType: 'paint',
          functionalGroupIds: templates.fgIds,
          templateIds: templates.tIds,
        });
      } else {
        return acc;
      }
    },
    [],
  );

  let wheelOptions = reduce(
    model.availableWheels,
    (acc, code) => {
      let details = wheels.byId[code];
      let templates = reduce(
        templatesForModel,
        (acc, template) => {
          if (template.designPackage.wheelOption.id === code) {
            let tIds = union(acc.tIds, [template.id]);
            let fIds = union(acc.fgIds, [template.functionalGroupId]);
            return {
              tIds: tIds,
              fgIds: fIds,
            };
          } else {
            return acc;
          }
        },
        { tIds: [], fgIds: [] },
      );

      if (templates.tIds.length > 0) {
        return acc.concat({
          code: details.id,
          id: details.id + 'wheel',
          description: details.description,
          optionType: 'wheel',
          functionalGroupIds: templates.fgIds,
          templateIds: templates.tIds,
        });
      } else {
        return acc;
      }
    },
    [],
  );

  let roofAndMirrorCapsOptions = reduce(
    model.availableRoofAndMirrorCaps,
    (acc, code) => {
      let details = roofAndMirrorCaps.byId[code];
      let templates = reduce(
        templatesForModel,
        (acc, template) => {
          if (
            template.designPackage.roofAndMirrorCapOption &&
            template.designPackage.roofAndMirrorCapOption.id === code
          ) {
            let tIds = union(acc.tIds, [template.id]);
            let fIds = union(acc.fgIds, [template.functionalGroupId]);
            return {
              tIds: tIds,
              fgIds: fIds,
            };
          } else {
            return acc;
          }
        },
        { tIds: [], fgIds: [] },
      );
      if (templates.tIds.length > 0) {
        return acc.concat({
          code: details.id,
          id: details.id + 'roofAndMirrorCap',
          description: details.description,
          optionType: 'roofAndMirrorCap',
          functionalGroupIds: templates.fgIds,
          templateIds: templates.tIds,
        });
      } else {
        return acc;
      }
    },
    [],
  );
  let trimOptions = reduce(
    model.availableTrims,
    (acc, code) => {
      let details = trims.byId[code];
      let templates = reduce(
        templatesForModel,
        (acc, template) => {
          if (template.designPackage.trimOption.id === code) {
            let tIds = union(acc.tIds, [template.id]);
            let fIds = union(acc.fgIds, [template.functionalGroupId]);
            return {
              tIds: tIds,
              fgIds: fIds,
            };
          } else {
            return acc;
          }
        },
        { tIds: [], fgIds: [] },
      );

      if (templates.tIds.length > 0) {
        return acc.concat({
          code: details.id,
          id: details.id + 'trim',
          description: details.description,
          optionType: 'trim',
          functionalGroupIds: templates.fgIds,
          templateIds: templates.tIds,
        });
      } else {
        return acc;
      }
    },
    [],
  );

  return concat(
    functionalOptions,
    interiorOptions,
    paintOptions,
    wheelOptions,
    trimOptions,
    roofAndMirrorCapsOptions,
  );
};

export const priceBands = ({ models }, { modelId }) =>
  models.byId[modelId] ? models.byId[modelId].priceBands : [];

export const staticDataLoaded = ({ models }, { modelId }) => {
  if (models.byId[modelId]) {
    return Boolean(models.byId[modelId].functionalGroups);
  }
  return false;
};

export const workingStateLoaded = ({ modelsWorkingState }, { modelId }) => {
  if (modelsWorkingState.byId[modelId]) {
    return Boolean(modelsWorkingState.byId[modelId].templatesWorkingState);
  }
  return false;
};

export const currentViewer = ({ modelsViewer }, { modelId }) => {
  const modelViewer = modelsViewer.byId[modelId];
  if (modelViewer && modelViewer.currentViewer) {
    return modelViewer.currentViewer;
  }
  return null;
};

export const lockedUntil = ({ modelsViewer }, { modelId }) => {
  const modelViewer = modelsViewer.byId[modelId];
  return modelViewer && modelViewer.lockedUntil;
};

export const locked = ({ modelsViewer }, { modelId }) => {
  const modelViewer = modelsViewer.byId[modelId];
  if (modelViewer && modelViewer.currentViewer) {
    const currentUser = getCurrentUser();
    return modelViewer.currentViewer.userId !== currentUser.userId;
  }
};

export const modelsLockedByCurrentUser = ({ modelsViewer }) => {
  const currentUser = getCurrentUser();
  return reduce(
    modelsViewer.byId,
    (result, value, model) => {
      if (currentUser) {
        let lockedByCurrentUser =
          value.currentViewer.userId === currentUser.userId;

        if (lockedByCurrentUser) {
          return result.concat(model);
        } else {
          return result;
        }
      }
    },
    [],
  );
};

export const modelHasTemplates = ({ models }, { modelId }) => {
  const model = models.byId[modelId];
  return Boolean(
    model && model.functionalGroups && model.functionalGroups.length,
  );
};

export const isModelActive = ({ models }) => models.modelIsActive;

export const hasModelLockExpired = ({ models }) => models.modelLockExpired;
